import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { BookOpenText, RefreshCw, Search, FileDown, ChevronLeft, ChevronRight, Trash2, IndianRupee, Users, Wheat, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { api } from "../lib/api";
import { getSchoolInfo, saveSchoolInfo } from "../lib/schoolInfo";
import { exportPanjiPDF } from "../lib/mdmExport";
import { formatCurrency } from "../lib/mdmData";

function monthRange(year, monthIndex) {
  const from = new Date(year, monthIndex, 1);
  const to = new Date(year, monthIndex + 1, 0);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { from_date: iso(from), to_date: iso(to) };
}

export default function PoshanPanji() {
  const initial = getSchoolInfo();
  const [udise, setUdise] = useState(initial.udise || "");
  const [schoolName, setSchoolName] = useState(initial.name || "");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [monthIndex, setMonthIndex] = useState(now.getMonth());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { from_date, to_date } = useMemo(() => monthRange(year, monthIndex), [year, monthIndex]);
  const monthLabel = useMemo(() => format(new Date(year, monthIndex, 1), "MMMM yyyy"), [year, monthIndex]);

  const totals = useMemo(() => {
    let ts = 0, tc = 0, tr = 0, td = 0, tl = 0;
    entries.forEach((e) => {
      ts += Number(e.summary?.total_students || 0);
      tc += Number(e.summary?.grand_total || 0);
      tr += Number(e.summary?.total_rice_kg || 0);
      if ((e.entry_type || "meal") === "ladoo") tl += Number(e.summary?.total_students || 0);
      else td += 1;
    });
    return { students: ts, cost: tc, rice: tr, mealDays: td, ladoos: tl };
  }, [entries]);

  const load = async () => {
    if (!/^\d{11}$/.test(udise.trim())) {
      toast.error("Enter a valid 11-digit UDISE to load your register");
      return;
    }
    setLoading(true);
    try {
      const rows = await api.listPanjiEntries(udise.trim(), from_date, to_date);
      setEntries(rows || []);
      setLoaded(true);
      // Save school info locally so the calculator picks it up too
      saveSchoolInfo({ name: schoolName.trim() || rows?.[0]?.school_name || "", udise: udise.trim() });
      if (!rows?.length) toast.info(`No entries found for ${monthLabel}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load register");
    } finally {
      setLoading(false);
    }
  };

  // Auto-reload when month/year changes if we've already loaded once
  useEffect(() => {
    if (loaded) load();
     
  }, [year, monthIndex]);

  const shiftMonth = (delta) => {
    let m = monthIndex + delta;
    let y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setYear(y); setMonthIndex(m);
  };

  const onExport = async () => {
    if (!entries.length) return toast.error("Nothing to export — load a month with entries first");
    try {
      const school = { name: schoolName || entries[0]?.school_name || "", udise };
      await exportPanjiPDF({ entries, school, from_date, to_date, monthLabel });
      toast.success("Poshan Panji PDF downloaded");
    } catch (e) {
      toast.error("Export failed"); console.error(e);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deletePanjiEntry(udise, deleteTarget.date_iso, deleteTarget.entry_type || "meal");
      setEntries((prev) => prev.filter((e) => !(e.date_iso === deleteTarget.date_iso && (e.entry_type || "meal") === (deleteTarget.entry_type || "meal"))));
      toast.success(`Removed ${deleteTarget.entry_type === "ladoo" ? "Ladoo" : "meal"} entry for ${deleteTarget.date_iso}`);
    } catch (e) {
      toast.error("Delete failed");
    } finally { setDeleteTarget(null); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="panji-page">
      <div>
        <div className="chip chip-green"><BookOpenText size={14}/> Poshan Panji Register</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Monthly meal register — per school</h1>
        <p className="mt-1 text-slate-600">Enter your school&rsquo;s 11-digit UDISE to view every report generated for that school. No sign-up, no password.</p>
      </div>

      {/* Search bar */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="panji-udise" className="text-xs uppercase tracking-widest font-bold text-slate-500">UDISE code (11 digits) *</Label>
            <Input id="panji-udise" value={udise} maxLength={11} inputMode="numeric"
                   onChange={(e) => setUdise(e.target.value.replace(/\D/g, "").slice(0, 11))}
                   placeholder="21010100999" className="h-11 mt-1.5" data-testid="panji-udise-input"/>
          </div>
          <div>
            <Label htmlFor="panji-school" className="text-xs uppercase tracking-widest font-bold text-slate-500">School name (optional)</Label>
            <Input id="panji-school" value={schoolName} onChange={(e) => setSchoolName(e.target.value)}
                   placeholder="Auto-filled from saved reports" className="h-11 mt-1.5" data-testid="panji-school-input"/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Month</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Button variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={() => shiftMonth(-1)} data-testid="panji-prev-month"><ChevronLeft size={16}/></Button>
              <div className="flex-1 h-11 rounded-md border border-slate-300 grid place-items-center font-display font-bold text-slate-900 bg-slate-50" data-testid="panji-month-label">
                <span className="flex items-center gap-2"><CalendarIcon size={14} className="text-emerald-700"/>{monthLabel}</span>
              </div>
              <Button variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={() => shiftMonth(1)} data-testid="panji-next-month"><ChevronRight size={16}/></Button>
            </div>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button onClick={load} disabled={loading} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="panji-load-btn">
            <Search size={16}/> {loading ? "Loading…" : "Load register"}
          </Button>
          <Button onClick={() => { setEntries([]); setLoaded(false); toast.info("Cleared"); }} variant="outline" data-testid="panji-clear-btn">
            <RefreshCw size={16}/> Clear
          </Button>
          <Button onClick={onExport} disabled={!entries.length} className="ml-auto bg-orange-600 hover:bg-orange-700 text-white" data-testid="panji-export-btn">
            <FileDown size={16}/> Export month PDF
          </Button>
        </div>
      </div>

      {/* KPIs + table */}
      {loaded && (
        <div className="mt-6 space-y-6 fade-rise">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Kpi icon={<CalendarIcon size={16}/>} label="Days recorded" value={String(totals.mealDays)} tint="slate" testid="panji-kpi-days"/>
            <Kpi icon={<Users size={16}/>} label="Total meals" value={String(totals.students - totals.ladoos)} tint="emerald" testid="panji-kpi-meals"/>
            <Kpi icon={<span className="text-sm">🧁</span>} label="Ladoos served" value={String(totals.ladoos)} tint="orange" testid="panji-kpi-ladoos"/>
            <Kpi icon={<IndianRupee size={16}/>} label="Total cost" value={formatCurrency(totals.cost)} tint="orange" testid="panji-kpi-cost"/>
            <Kpi icon={<Wheat size={16}/>} label="Total rice" value={`${totals.rice.toFixed(2)} kg`} tint="emerald" testid="panji-kpi-rice"/>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="chip chip-green">📒 Register</div>
                <div className="mt-2 font-display font-bold text-lg text-slate-900">Daily entries · {monthLabel}</div>
                {entries.length > 0 && entries[0]?.school_name && (
                  <div className="text-sm text-slate-500">Recorded for: <b>{entries[0].school_name}</b> · UDISE {udise}</div>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <Th>Date</Th><Th>Day</Th><Th>Type</Th><Th>Menu</Th>
                    <Th className="text-center">Bal</Th><Th className="text-center">Pri</Th>
                    <Th className="text-center">UP</Th><Th className="text-center">HS</Th>
                    <Th className="text-center">Total</Th><Th className="text-right">Rice (kg)</Th>
                    <Th className="text-right">Cost (₹)</Th><Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 && (
                    <tr><td colSpan={12} className="p-8 text-center text-slate-500" data-testid="panji-empty">No entries in this month — generate a report on the Calculator or Ladoo pages to populate this register.</td></tr>
                  )}
                  {entries.map((e) => {
                    const isLadoo = (e.entry_type || "meal") === "ladoo";
                    const rowKey = `${e.date_iso}-${e.entry_type || "meal"}`;
                    return (
                      <tr key={rowKey} className={`border-b border-slate-100 hover:bg-slate-50 ${isLadoo ? "bg-orange-50/40" : ""}`} data-testid={`panji-row-${rowKey}`}>
                        <Td className="font-semibold text-slate-900">{format(parseISO(e.date_iso), "d MMM")}</Td>
                        <Td>{e.weekday_name}</Td>
                        <Td>
                          {isLadoo ? (
                            <span className="chip chip-saffron text-[10px]" data-testid={`panji-type-ladoo-${e.date_iso}`}>🧁 Ladoo</span>
                          ) : (
                            <span className="chip chip-green text-[10px]" data-testid={`panji-type-meal-${e.date_iso}`}>🍛 Meal</span>
                          )}
                        </Td>
                        <Td className="text-slate-600 max-w-[220px] truncate" title={e.menu_display}>{e.menu_display}</Td>
                        <Td className="text-center">{e.counts?.balvatika ?? 0}</Td>
                        <Td className="text-center">{e.counts?.primary ?? 0}</Td>
                        <Td className="text-center">{e.counts?.upper_primary ?? 0}</Td>
                        <Td className="text-center">{e.counts?.high_school ?? 0}</Td>
                        <Td className="text-center font-semibold">{e.summary?.total_students ?? 0}</Td>
                        <Td className="text-right">{Number(e.summary?.total_rice_kg || 0).toFixed(2)}</Td>
                        <Td className="text-right font-semibold text-slate-900">{formatCurrency(e.summary?.grand_total || 0)}</Td>
                        <Td className="text-right">
                          <button onClick={() => setDeleteTarget(e)} className="text-slate-400 hover:text-red-600" title="Remove entry" data-testid={`panji-del-${rowKey}`}>
                            <Trash2 size={14}/>
                          </button>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
                {entries.length > 0 && (
                  <tfoot>
                    <tr className="bg-emerald-50">
                      <Td colSpan={8} className="font-bold text-emerald-900 text-right">Month total</Td>
                      <Td className="text-center font-bold text-emerald-900">{totals.students}</Td>
                      <Td className="text-right font-bold text-emerald-900">{totals.rice.toFixed(2)}</Td>
                      <Td className="text-right font-display font-extrabold text-lg text-emerald-800" data-testid="panji-grand-total">{formatCurrency(totals.cost)}</Td>
                      <Td></Td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="panji-delete-dialog">
          <DialogHeader>
            <DialogTitle>Remove this entry?</DialogTitle>
            <DialogDescription>Delete this Poshan Panji row. You can re-create it any time by generating the report again.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            The entry for <b>{deleteTarget?.date_iso}</b> ({deleteTarget?.weekday_name}) will be deleted from the register. You can re-create it by generating that date&rsquo;s report again.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="panji-confirm-delete">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon, label, value, tint, testid }) {
  const bg = tint === "emerald" ? "border-emerald-200 bg-emerald-50" : tint === "orange" ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-3 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">{icon}{label}</div>
      <div className="mt-1 font-display font-extrabold text-lg text-slate-900">{value}</div>
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`p-3 text-xs uppercase tracking-wider font-bold text-slate-500 ${className}`}>{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`p-3 text-slate-700 ${className}`}>{children}</td>;
